import React, { useState, useRef, useContext, useEffect } from "react";
import { AuthContext } from "../context/AuthContext";
import { updateProfileApi, SERVER_URL, sendEmailVerificationApi, verifyEmailOtpApi } from "../utils/api";
import { useNavigate } from "react-router-dom";
import "../css/profile.css";

export default function EditProfileForm() {
  const { user, saveUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const currentUser = user || {};

  const [form, setForm] = useState({
    name: currentUser.name || "",
    phone: currentUser.phone || "",
    address: currentUser.address || "",
    vehicle: currentUser.vehicle || "",
    gender: currentUser.gender || ""
  });

  const [avatarFile, setAvatarFile] = useState(null);
  const [preview, setPreview] = useState("/default-avatar.png");
  const fileRef = useRef();
  const [loading, setLoading] = useState(false);

  // OTP & Support States
  const [otpSent, setOtpSent] = useState(false);
  const [emailOtp, setEmailOtp] = useState("");
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyMsg, setVerifyMsg] = useState("");
  const [showSupport, setShowSupport] = useState(false); // ✅ Controls the support box

  useEffect(() => {
    if (currentUser.avatar) {
      setPreview(currentUser.avatar.startsWith("http") ? currentUser.avatar : `${SERVER_URL}${currentUser.avatar}`);
    }
  }, [currentUser.avatar]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarFile(file);
    setPreview(URL.createObjectURL(file)); 
  };

  const handleSendOtp = async () => {
    setVerifyLoading(true);
    setVerifyMsg("");
    setShowSupport(false); // Hide support box initially

    try {
      const token = localStorage.getItem("travelo_token");
      await sendEmailVerificationApi(currentUser._id || currentUser.id, token);
      setOtpSent(true);
      setVerifyMsg("Code sent successfully!");
    } catch (err) {
      const errorText = err.response?.data?.message || "Failed to send OTP.";
      setVerifyMsg(errorText);

      // ✅ Auto-show support box if the email doesn't exist or is invalid
      if (errorText.includes("does not exist") || errorText.includes("❌")) {
        setShowSupport(true);
      }
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleConfirmOtp = async () => {
    if (emailOtp.length !== 6) return setVerifyMsg("Enter a 6-digit code.");
    setVerifyLoading(true);
    try {
      const token = localStorage.getItem("travelo_token");
      const res = await verifyEmailOtpApi(currentUser._id || currentUser.id, emailOtp, token);
      saveUser(res.user, token);
      setOtpSent(false);
      setVerifyMsg("✅ Email Verified!");
    } catch (err) {
      setVerifyMsg("❌ " + (err.response?.data?.message || "Invalid code."));
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem("travelo_token");
      if (!token) throw new Error("No token found");
      const data = new FormData();
      data.append("address", form.address);
      data.append("vehicle", form.vehicle);
      data.append("gender", form.gender);
      if (avatarFile) data.append("avatar", avatarFile);

      const resData = await updateProfileApi(data, token);
      saveUser(resData.user || resData, token);
      alert("Profile updated successfully! ✅");
      navigate("/profile"); 
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!user) return <div className="loading-overlay">Syncing...</div>;

  return (
    <div className="edit-modal">
      <div className="edit-card">
        {loading && (
          <div className="loading-overlay">
            <div className="pd-loader"></div>
            <span>Saving your changes...</span>
          </div>
        )}
        
        <button type="button" className="close-x" onClick={() => navigate("/profile")}>
          &times;
        </button>

        <div className="edit-header-minimal">
          <h2 className="edit-title">Personal Settings</h2>
          <p className="edit-subtitle">Manage your account information and preferences</p>
        </div>

        <form className="edit-form" onSubmit={handleSubmit}>
          
          <div className="avatar-section">
            <div className="avatar-wrapper">
              <img src={preview} alt="avatar preview" className="avatar-img" />
              <button type="button" className="avatar-btn" onClick={() => fileRef.current.click()}>
                <span role="img" aria-label="camera">📸</span>
              </button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden-input" onChange={handleFile} />
            </div>
            <p className="avatar-hint">Click the camera icon to update your photo</p>
          </div>

          <div className="fields-grid">
            <div className="field">
              <label>Full Name</label>
              <div className="readonly-input">
                <span className="field-icon">👤</span>
                {form.name}
              </div>
            </div>

            <div className="field">
              <label>Phone Number</label>
              <div className="readonly-input">
                <span className="field-icon">📞</span>
                {form.phone}
              </div>
            </div>

            {/* EMAIL VERIFICATION BOX */}
            <div className="field email-verify-container">
              <label>Email Address</label>
              <div className="email-status-row">
                <div className="readonly-input" style={{ flex: 1 }}>
                  <span className="field-icon">📧</span>
                  {currentUser.email}
                </div>
                
                {currentUser.isEmailVerified ? (
                  <div className="verified-badge">
                    <span>Verified</span>
                  </div>
                ) : (
                  <button type="button" onClick={handleSendOtp} disabled={verifyLoading || otpSent} className="verify-trigger-btn">
                    {verifyLoading ? "..." : "Verify Now"}
                  </button>
                )}
              </div>

              {otpSent && !currentUser.isEmailVerified && (
                <div className="otp-input-wrapper fade-in">
                  <input 
                    type="text" 
                    placeholder="Enter 6-digit code" 
                    maxLength={6}
                    value={emailOtp}
                    onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, ''))}
                  />
                  <button type="button" onClick={handleConfirmOtp} disabled={verifyLoading} className="confirm-btn">
                    Confirm
                  </button>
                </div>
              )}

              {verifyMsg && <p className={`verify-feedback ${verifyMsg.includes("❌") ? "error" : "success"}`}>{verifyMsg}</p>}

              {/* ✅ AUTO-TRIGGERED SUPPORT BOX */}
              {!currentUser.isEmailVerified && showSupport && (
                <div className="support-alert-box fade-in">
                  <div className="support-icon">🎧</div>
                  <div className="support-content">
                    <h4>Invalid or Incorrect Email?</h4>
                    <p>It looks like this email does not exist. For security reasons, please contact the Vyango Support team to link a new email to your account.</p>
                    
                    <div className="support-actions">
                      <a 
                        href={`mailto:support@vyango.com?subject=Email Change Request - Phone: ${currentUser.phone}&body=Hello Vyango Team,%0D%0A%0D%0AMy registered email is invalid and I need to change it.%0D%0A%0D%0AMy registered phone number is: ${currentUser.phone}%0D%0ACurrently Registered Email: ${currentUser.email}%0D%0A%0D%0ANew Email to link: (Type your new email here)%0D%0A%0D%0AThank you!`} 
                        className="support-mailto-btn"
                      >
                        ✉️ Email Vyango Support
                      </a>
                      <span className="support-or"> or call <strong>6264921908</strong></span>
                    </div>
                  </div>
                </div>
              )}

              {/* Manual Trigger just in case they have a typo but it's a real email */}
              {!currentUser.isEmailVerified && !showSupport && (
                <button type="button" className="text-link-btn" onClick={() => setShowSupport(true)} style={{marginTop: "8px", background: "none", border: "none", color: "#1a73e8", cursor: "pointer", textDecoration: "underline", fontSize: "13px", textAlign: "left", padding: 0}}>
                  Wrong email? Request a change
                </button>
              )}
            </div>

            <div className="field">
              <label>Gender</label>
              <div className="input-with-icon">
                <span className="field-icon">🚻</span>
                <select name="gender" value={form.gender} onChange={handleChange}>
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div className="field">
              <label>Current Address</label>
              <div className="input-with-icon">
                <span className="field-icon">📍</span>
                <input name="address" value={form.address} onChange={handleChange} placeholder="Home or city address" />
              </div>
            </div>

            <div className="field">
              <label>Vehicle Details</label>
              <div className="input-with-icon">
                <span className="field-icon">🚗</span>
                <input name="vehicle" value={form.vehicle} onChange={handleChange} placeholder="e.g., Honda City (MP-04-XX-0000)" />
              </div>
            </div>
          </div>

          <div className="form-actions-sticky">
            <button type="button" className="btn-cancel" onClick={() => navigate("/profile")}>
              Discard
            </button>
            <button type="submit" className="btn-save" disabled={loading}>
              Save Profile
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}